import { useState, useEffect, useRef, ReactNode } from "react";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { Editor } from "primereact/editor";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { httpPost, httpUpdate, httpDelete, useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { Icons, MEMBER_KEY, Messages, USER_DETAIL } from "../../../utils/constants";
import { IAnalysisSummaryResponse } from "../../../interface/analysisSummary";
import REIDataTable from "../../../components/ui/dataTable";
import { REIButton, REIProgressSpinner, REIToastContainer } from "../../../components/ui";
import { confirmPopup } from "primereact/confirmpopup";

export interface IMemberPropertyComment {
  Comment: string;
  CommentId: number;
  DateCreated: string;
}

interface PropertyData {
  watchListId: string;
}

interface AddNote {
  Comment: string;
  CommentId: number;
  MemberId: string;
}

const EditorAnalyser: React.FC<PropertyData> = ({ watchListId }) => {
  const [notes, setNotes] = useState<IMemberPropertyComment[]>([]);
  const [isedittorHidden, setIsedittorHidden] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editorText, setEditorText] = useState<string>("");
  const [refreshData, setRefreshData] = useState(false);
  const toastRef = useRef<Toast>(null);
  let addnote = useRef<{ Comment: string; MemberId: string | null; MemberPropertyId: string }>();
  let selectednote = useRef<AddNote>();
  const memberId = localStorage.getItem(MEMBER_KEY);
  const propertySummary = useSelector((state: RootState) => state.propertyDetails.propertySummary);
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");

  useEffect(() => {
    setLoading(true);
    useHttpGets(
      `${ApiEndPoints.analyser}/PropertyDetailSummary?MemberId=${memberId}&PropertyAnalyserId=${analysisSummary.PropertyAnalyserId}&MemberCountryCode=${memberDetail.Country}`,
      undefined
    )
      .then((result) => {
        if (result.status === 200 && result.data) {
          const propertyComments = result.data?.PropertyComments ?? [];
          setNotes(
            propertyComments.length > 0
              ? propertyComments.map((comment) => ({
                  Comment: comment.Comment,
                  CommentId: comment.CommentId,
                  DateCreated: comment.DateCreated,
                }))
              : []
          );
          setLoading(false);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [refreshData]);

  useEffect(() => {
    setNotes([]);
    if (propertySummary?.comments && propertySummary.comments.length > 0) {
      setNotes(propertySummary.comments);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [propertySummary?.comments]);

  const handleEdit = (event: React.MouseEvent<HTMLElement>, row: AddNote) => {
    setEditorText(row.Comment);
    selectednote.current = row;
    setIsedittorHidden(false);
  };

  const handleDelete = (row: AddNote) => {
    setLoading(true);
    httpDelete(`${ApiEndPoints.analyserComments}?memberId=${memberId}&CommentId=${row.CommentId}`, undefined).then(
      (result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.DELETE_SUCCESS,
            detail: "Note Deleted Successfully",
            life: 2000,
          });
          setRefreshData(!refreshData);
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: Messages.DELETE_ERROR,
            detail: Messages.ERROR,
            life: 2000,
          });
          setLoading(false);
        }
      }
    );
  };

  const handleSave = async () => {
    if (selectednote.current != null) {
      handleEditNote(editorText);
    } else {
      handleAddNote(editorText);
    }
  };

  const handleAddNote = async (newText: string) => {
    setLoading(true);
    addnote.current = {
      Comment: newText,
      MemberId: localStorage.getItem(MEMBER_KEY),
      MemberPropertyId: watchListId,
    };

    await httpPost(`${ApiEndPoints.analyserComments}`, addnote.current).then((result) => {
      if (result.status == 200) {
        setIsedittorHidden(true);
        setEditorText("");
        toastRef.current?.show({
          severity: "success",
          summary: Messages.SUCCESS,
          detail: "Note Added Successfully",
          life: 2000,
        });
        setRefreshData(!refreshData);
      } else {
        setEditorText("");
        setIsedittorHidden(true);
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setLoading(false);
      }
    });
  };

  const handleEditNote = async (newText: string) => {
    if (!selectednote.current) return;
    if (newText === selectednote.current.Comment) {
      setIsedittorHidden(true);
      setEditorText("");
      selectednote.current = undefined;
      return;
    }
    setLoading(true);
    selectednote.current.Comment = newText;
    await httpUpdate(`${ApiEndPoints.analyserComments}`, {
      Comment: selectednote.current.Comment,
      CommentId: selectednote.current.CommentId,
      MemberId: localStorage.getItem(MEMBER_KEY),
    }).then((result) => {
      if (result.status == 200) {
        setIsedittorHidden(true);
        setEditorText("");
        selectednote.current = undefined;
        toastRef.current?.show({
          severity: "success",
          summary: Messages.SUCCESS,
          detail: "Note Updated Successfully",
          life: 2000,
        });
        setRefreshData(!refreshData);
      } else {
        setEditorText("");
        setIsedittorHidden(true);
        selectednote.current = undefined;
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setLoading(false);
      }
    });
  };

  const handleEditorToggle = () => {
    setIsedittorHidden(!isedittorHidden);
    selectednote.current = undefined;
    setEditorText("");
  };

  const columns = [
    {
      header: "",
      field: "DateCreated",
      position: "center",
      className: "text-black-300 text-center",
    },
    {
      header: "",
      field: "Comment",
      position: "center",
      className: "w-34 text-ellipsis",
      bodyTemplate: (rowData: AddNote): ReactNode => <div dangerouslySetInnerHTML={{ __html: rowData.Comment }}></div>,
    },
    {
      field: "",
      type: "custom",

      onClick: handleEdit,
      bodyTemplate: (rowData: AddNote) => (
        <i
          id={`anot-icon-edit-${rowData.CommentId}`}
          data-testid={`anot-icon-edit-${rowData.CommentId}`}
          aria-label="Edit Note"
          title="Edit Note"
          className="pi pi-pencil text-[#4E9FBA] cursor-pointer w-1/5 text-sm text-end"
        ></i>
      ),
    },
    {
      field: "",
      type: "custom",
      bodyTemplate: (rowData: AddNote) => (
        <i
          id={`anot-icon-delete-${rowData.CommentId}`}
          data-testid={`anot-icon-delete-${rowData.CommentId}`}
          aria-label="Delete Note"
          title="Delete Note"
          className={`pi pi-times text-[#4E9FBA] w-1/5 text-sm ${
            selectednote.current?.CommentId === rowData.CommentId ? "opacity-50 pointer-events-none" : "cursor-pointer"
          }`}
          onClick={(e) => {
            if (selectednote.current?.CommentId !== rowData.CommentId) {
              confirmDelete(e, rowData);
            }
          }}
        ></i>
      ),
    },
  ];

  const renderHeader = () => {
    return (
      <span className="ql-formats">
        <button id="anot-btn-bold" data-testid="anot-btn-bold" className="ql-bold" aria-label="Bold"></button>
        <button id="anot-btn-italic" data-testid="anot-btn-italic" className="ql-italic" aria-label="Italic"></button>
        <button
          id="anot-btn-underline"
          data-testid="anot-btn-underline"
          className="ql-underline"
          aria-label="Underline"
        ></button>
      </span>
    );
  };

  const confirmDelete = (event: React.MouseEvent, row: Notes) => {
    confirmPopup({
      target: event.currentTarget,
      message: Messages.CONFIRMATION_MESSAGE,
      icon: Icons.INFO_ICON,
      accept: () => handleDelete(row),
    });
  };

  return (
    <div>
      <REIToastContainer toastReference={toastRef} />
      <>
        <Card title="NOTES" className="card">
          {loading && <REIProgressSpinner />}
          {!loading && (
            <div className="max-h-36 overflow-auto">
              <REIDataTable
                data={notes}
                columns={columns}
                customclass="panel-box datagrid  border rounded-md mt-4 hide-datatable-header"
              />
            </div>
          )}
          <div className="m-3">
            <REIButton
              id="anot-btn-addnote"
              classNames="text-xs font-thin border-none p-1 borderRadius"
              onClick={handleEditorToggle}
              label="+ Add note"
              disabled={loading}
            ></REIButton>
          </div>
          <div className="card border border-slate-300" hidden={isedittorHidden}>
            <Editor
              headerTemplate={renderHeader()}
              value={editorText}
              onTextChange={(e) => setEditorText(e.htmlValue)}
            />
            <REIButton
              id="anot-btn-save"
              classNames="items-end text-xs p-1 m-1 "
              disabled={
                editorText == "" || editorText == null || editorText === selectednote.current?.Comment || loading
              }
              onClick={handleSave}
              label="Save"
            ></REIButton>
            <REIButton
              id="anot-btn-cancel"
              classNames="items-end text-xs p-1 m-1"
              disabled={loading}
              onClick={handleEditorToggle}
              label="Cancel"
            />
          </div>
        </Card>
      </>
    </div>
  );
};

export default EditorAnalyser;
